import { useState } from 'react'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Typography } from '@/components/ui/typography'
import {
  type CompanyPlReview,
  type CompanyPlReviewRow,
  type PlReviewPeriod,
  type PlReviewStatus,
  plReviewPeriods,
} from '@/features/actual-vs-budget/pl-review'
import { cn } from '@/lib/utils'

const statusClasses: Record<PlReviewStatus, string> = {
  favorable: 'text-green-700',
  unfavorable: 'text-destructive',
  neutral: 'text-primary',
  unavailable: 'text-muted-foreground',
}

const statusLabels: Record<PlReviewStatus, string> = {
  favorable: 'Favorable variance',
  unfavorable: 'Unfavorable variance',
  neutral: 'Neutral variance',
  unavailable: 'Variance unavailable',
}

type PlReasonColumn = 'actual' | 'budget' | 'priorYear' | 'forecast'

type SharedReasons = Record<PlReasonColumn, string | null>

// A missing-data reason that repeats identically on every row of a column is a
// column-level fact, not a per-row one (e.g. Full Year Actuals all read "Fiscal
// year not complete"). Hoist it to a single header caption instead of stamping
// it on all ~7 rows.
function sharedColumnReason(
  rows: readonly CompanyPlReviewRow[],
  getReason: (row: CompanyPlReviewRow) => string | null
): string | null {
  const reasons = rows
    .map(getReason)
    .filter((reason): reason is string => reason !== null)
  if (reasons.length < 2) return null
  const [first] = reasons
  return reasons.every((reason) => reason === first) ? first : null
}

function isPlReviewPeriod(value: string): value is PlReviewPeriod {
  return plReviewPeriods.some(({ key }) => key === value)
}

function MissingDetail({ reason }: { reason: string | null }) {
  if (!reason) return null

  return (
    <Typography
      as="span"
      variant="body-sm"
      className="mt-0.5 block max-w-32 whitespace-normal text-[10px] leading-tight text-muted-foreground"
    >
      {reason}
    </Typography>
  )
}

function ValueCell({
  display,
  reason = null,
  secondary,
  status,
}: {
  display: string
  reason?: string | null
  secondary?: string
  status?: PlReviewStatus
}) {
  return (
    <TableCell className="px-3 py-2.5 text-right align-top tabular-nums">
      <Typography
        as="span"
        variant="body-sm"
        aria-label={status ? `${display}, ${statusLabels[status]}` : undefined}
        className={cn(
          'block font-medium',
          status ? statusClasses[status] : 'text-primary'
        )}
      >
        {display}
      </Typography>
      {secondary && secondary !== '—' ? (
        <Typography
          as="span"
          variant="body-sm"
          className="mt-0.5 block text-[10px] text-muted-foreground"
        >
          {secondary}
        </Typography>
      ) : null}
      <MissingDetail reason={reason} />
    </TableCell>
  )
}

function MatchedPeriodCells({
  row,
  sharedReasons,
}: {
  row: CompanyPlReviewRow
  sharedReasons: SharedReasons
}) {
  return (
    <>
      <ValueCell
        display={row.actualDisplay}
        reason={sharedReasons.actual ? null : row.actualMissingReason}
      />
      <ValueCell
        display={row.budgetDisplay}
        reason={sharedReasons.budget ? null : row.budgetMissingReason}
      />
      <ValueCell
        display={row.priorYearActualDisplay}
        reason={sharedReasons.priorYear ? null : row.priorYearMissingReason}
      />
      <ValueCell
        display={row.varianceVsBudgetDisplay}
        status={row.budgetStatus}
      />
      <ValueCell
        display={row.varianceVsBudgetPercentDisplay}
        status={
          row.varianceVsBudgetPercentDisplay === '—'
            ? undefined
            : row.budgetStatus
        }
      />
      <ValueCell
        display={row.varianceVsPriorYearDisplay}
        status={
          row.varianceVsPriorYearDisplay === '—'
            ? undefined
            : row.priorYearStatus
        }
      />
    </>
  )
}

function FullYearCells({
  row,
  sharedReasons,
}: {
  row: CompanyPlReviewRow
  sharedReasons: SharedReasons
}) {
  return (
    <>
      <MatchedPeriodCells row={row} sharedReasons={sharedReasons} />
      <ValueCell
        display={row.forecastDisplay}
        reason={sharedReasons.forecast ? null : row.forecastMissingReason}
      />
      <ValueCell
        display={row.forecastVsBudgetDisplay}
        status={row.forecastStatus}
      />
    </>
  )
}

const matchedPeriodHeaders = [
  'Actual',
  'Budget',
  'Prior-year Actual',
  'Variance vs Budget',
  'Variance vs Budget %',
  'Variance vs Prior Year %',
] as const

const fullYearHeaders = [
  'Actual',
  'Budget',
  'Prior-year Actual',
  'Variance vs Budget',
  'Variance vs Budget %',
  'Variance vs Prior Year %',
  'Forecast',
  'Forecast vs Budget',
] as const

// The shared tab active state is a thin white/outline box; on this dense table
// it is easy to miss which period is selected. Give the active period a solid
// navy fill so it reads unambiguously.
const selectedPeriodTabClasses =
  'data-[state=active]:border-primary data-[state=active]:bg-primary data-[state=active]:font-medium data-[state=active]:text-primary-foreground'

export function PlReviewTable({ review }: { review: CompanyPlReview }) {
  const [selectedPeriod, setSelectedPeriod] = useState<PlReviewPeriod>('fytd')
  const period = review.periods[selectedPeriod]
  const isFullYear = selectedPeriod === 'full-year'
  const headers = isFullYear ? fullYearHeaders : matchedPeriodHeaders
  const sharedReasons: SharedReasons = {
    actual: sharedColumnReason(period.rows, (row) => row.actualMissingReason),
    budget: sharedColumnReason(period.rows, (row) => row.budgetMissingReason),
    priorYear: sharedColumnReason(
      period.rows,
      (row) => row.priorYearMissingReason
    ),
    forecast: sharedColumnReason(
      period.rows,
      (row) => row.forecastMissingReason
    ),
  }
  const headerReason: Record<string, string | null> = {
    Actual: sharedReasons.actual,
    Budget: sharedReasons.budget,
    'Prior-year Actual': sharedReasons.priorYear,
    Forecast: sharedReasons.forecast,
  }

  return (
    <section
      aria-labelledby="pl-review-heading"
      className="overflow-hidden rounded-lg border border-primary/10 bg-background"
    >
      <div className="border-b border-primary/10 bg-muted/20 px-5 py-4">
        <div className="flex flex-col justify-between gap-3 xl:flex-row xl:items-end">
          <div>
            <Typography id="pl-review-heading" as="h3" variant="h5">
              P&amp;L comparison
            </Typography>
            <Typography
              variant="body-sm"
              aria-live="polite"
              className="mt-1 text-muted-foreground"
            >
              {period.contextLabel} · values in USD millions unless noted
            </Typography>
          </div>

          <Tabs
            value={selectedPeriod}
            onValueChange={(value) => {
              if (isPlReviewPeriod(value)) setSelectedPeriod(value)
            }}
          >
            <TabsList
              aria-label="P&L comparison period"
              className="h-auto w-full justify-start overflow-x-auto sm:w-auto"
            >
              {plReviewPeriods.map(({ key, label }) => (
                <TabsTrigger
                  key={key}
                  id={`pl-review-tab-${key}`}
                  aria-controls="pl-review-tabpanel"
                  value={key}
                  className={selectedPeriodTabClasses}
                >
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        <Typography variant="body-sm" className="mt-3 text-muted-foreground">
          Incomplete Actuals are labeled Fiscal year not complete; missing
          comparisons remain explicit as Budget data unavailable or Forecast
          unavailable.
        </Typography>
      </div>

      <div
        id="pl-review-tabpanel"
        role="tabpanel"
        aria-labelledby={`pl-review-tab-${selectedPeriod}`}
        tabIndex={0}
        className="overflow-x-auto"
      >
        <Table disableWrapper className="min-w-5xl">
          <TableCaption className="sr-only">
            {period.label} profit and loss comparison for {period.contextLabel}
          </TableCaption>
          <TableHeader>
            <TableRow className="bg-primary/[0.04] hover:bg-primary/[0.04]">
              <TableHead className="sticky left-0 z-20 w-52 bg-muted px-4">
                P&amp;L line item
              </TableHead>
              {headers.map((header) => {
                const caption = headerReason[header]
                return (
                  <TableHead
                    key={header}
                    className="h-auto min-w-32 whitespace-normal px-3 py-2 text-right align-bottom"
                  >
                    {header}
                    {caption ? (
                      <Typography
                        as="span"
                        variant="body-sm"
                        className="mt-1 block text-[10px] font-normal leading-tight text-muted-foreground"
                      >
                        {caption}
                      </Typography>
                    ) : null}
                  </TableHead>
                )
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {!period.hasMatchedData ? (
              <TableRow>
                <TableCell colSpan={headers.length + 1} className="h-28 px-4">
                  <Typography
                    variant="body-sm"
                    className="text-center text-muted-foreground"
                  >
                    No data is available for this period.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
            {period.hasMatchedData
              ? period.rows.map((row) => (
                  <TableRow
                    key={row.key}
                    className={cn(
                      row.emphasis === 'subtotal' && 'bg-muted/20 font-medium',
                      row.emphasis === 'total' &&
                        'border-t-2 border-primary/20 bg-primary/[0.04] font-semibold'
                    )}
                  >
                    <TableHead
                      scope="row"
                      className={cn(
                        'sticky left-0 z-10 h-auto w-52 bg-background px-4 py-2.5',
                        row.emphasis !== 'standard' && 'bg-muted',
                        row.indent === 1 && 'pl-8'
                      )}
                    >
                      <Typography
                        as="span"
                        variant="body-sm"
                        className="font-medium text-primary"
                      >
                        {row.label}
                      </Typography>
                    </TableHead>
                    {isFullYear ? (
                      <FullYearCells row={row} sharedReasons={sharedReasons} />
                    ) : (
                      <MatchedPeriodCells
                        row={row}
                        sharedReasons={sharedReasons}
                      />
                    )}
                  </TableRow>
                ))
              : null}
          </TableBody>
        </Table>
      </div>
    </section>
  )
}
